import { useState } from 'react';
import { Activity } from 'lucide-react';

import { BarChart } from '@/components/ui/BarChart';
import { LineChart } from '@/components/ui/LineChart';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import { activityApi } from '@/features/activity/api/activity.api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatDate, formatDuration, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TrendBucket, TrendView } from '@/types/usage';
import { dayLabels } from '../reports/utils';

const VIEWS = [
  { value: 'day', label: 'By date' },
  { value: 'month', label: 'By month' },
  { value: 'weekday', label: 'By weekday' },
] as const;

const PERIODS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
] as const;

const WEEKDAY = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTH = new Intl.DateTimeFormat('en-IN', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const MONTH_SHORT = new Intl.DateTimeFormat('en-IN', {
  month: 'short',
  timeZone: 'UTC',
});

/** A bucket's full label for the tooltip and its short one for the axis. */
function labelsFor(view: TrendView, key: string): { label: string; axisLabel: string } {
  if (view === 'day') return dayLabels(key);
  if (view === 'month') {
    const date = new Date(`${key}-01T00:00:00Z`);
    return { label: MONTH.format(date), axisLabel: MONTH_SHORT.format(date) };
  }
  const name = WEEKDAY[Number(key)] ?? key;
  return { label: `An average ${name}`, axisLabel: name.slice(0, 3) };
}

function minutesLabel(minutes: number): string {
  return formatDuration(Math.round(minutes)) ?? '0 mins';
}

/**
 * Logins, people and time in the app over time — by date, by month, or the
 * average day of the week.
 *
 * Three charts on one shared time axis, each answering one question: how many
 * sign-ins (web and app stacked), how many people (who signed in, and who
 * actually used the app), and how much time. The figures above them are the
 * whole period at a glance.
 *
 * "Active" comes from the minute counter, which only began on the day it was
 * installed, and only the web app reports into it so far — the line is short
 * and the mobile app is not in it, and the panel says so rather than letting
 * the reader take a quiet chart for a quiet platform.
 */
export function ActivityTrendPanel() {
  const [view, setView] = useState<TrendView>('day');
  const [period, setPeriod] = useState<'7' | '30' | '90'>('30');
  const days = Number(period) as 7 | 30 | 90;

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => activityApi.trend(view, days), [view, days]);

  // Switching view re-renders once with the previous view's buckets still
  // held, before the new request starts. Their keys do not parse as the new
  // view's — a `YYYY-MM-DD` read as a month is an Invalid Date, and formatting
  // that throws — so a response is shown only under the view it was fetched for.
  const data = response?.view === view ? response : null;

  const buckets = data?.buckets ?? [];
  const isAverage = view === 'weekday';
  const points = (pick: (bucket: TrendBucket) => Record<string, number | null>) =>
    buckets.map((bucket) => ({
      ...labelsFor(view, bucket.key),
      values: pick(bucket),
    }));

  const totalLogins = buckets.reduce((sum, bucket) => sum + bucket.logins, 0);
  const totalMinutes = buckets.reduce((sum, bucket) => sum + bucket.minutes, 0);
  const busiest = buckets.reduce<TrendBucket | null>(
    (best, bucket) => (bucket.logins > (best?.logins ?? 0) ? bucket : best),
    null,
  );
  const peakPeople = Math.max(0, ...buckets.map((bucket) => bucket.loggedInPeople));
  const unit = view === 'month' ? 'month' : 'day';

  const figures = [
    {
      label: isAverage ? 'Logins on an average day' : 'Logins',
      value: formatNumber(isAverage ? Math.round(totalLogins / 7) : totalLogins),
    },
    {
      label: isAverage ? 'Busiest weekday' : `Busiest ${unit}`,
      value: busiest ? labelsFor(view, busiest.key).label.replace('An average ', '') : '—',
    },
    {
      label: `Most people in a ${unit}`,
      value: formatNumber(Math.round(peakPeople)),
    },
    {
      label: isAverage ? 'Time on an average day' : 'Time in the app',
      value: minutesLabel(isAverage ? totalMinutes / 7 : totalMinutes),
    },
  ];

  return (
    <section className="flex flex-col gap-5 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.03)]" aria-labelledby="activity-trend-heading">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
        <div className="flex items-center gap-3">
          <span
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-[#5b52f9] dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50 shadow-2xs"
            aria-hidden
          >
            <Activity className="h-5 w-5" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h2 id="activity-trend-heading" className="text-[16px] font-black text-slate-900 dark:text-white tracking-tight">
                Daily activity
              </h2>
              {data && (
                <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-[11px] font-bold text-slate-600 dark:text-slate-400">
                  {formatDate(data.from)} – {formatDate(data.to)}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
              Logins, people and time in the app across every school.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Tabs
            options={VIEWS}
            value={view}
            onChange={setView}
            label="Group activity by"
            variant="segmented"
          />
          {view !== 'month' && (
            <Tabs
              options={PERIODS}
              value={period}
              onChange={setPeriod}
              label="Activity period"
              variant="segmented"
            />
          )}
        </div>
      </div>

      {error ? (
        <QueryError title="Could not load activity" error={error} onRetry={refetch} />
      ) : isLoading || !data ? (
        <div className="flex flex-col gap-4" role="status" aria-label="Loading activity">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-16 w-full rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-56 w-full rounded-xl" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-3.5 lg:grid-cols-4">
            {figures.map((figure, idx) => {
              const STYLES = [
                {
                  wrapper: 'border-blue-100 dark:border-blue-900/50 bg-gradient-to-b from-blue-50/70 via-blue-50/30 to-white dark:from-blue-950/30 dark:to-slate-900 shadow-[0_4px_14px_rgba(59,130,246,0.06)]',
                },
                {
                  wrapper: 'border-indigo-100 dark:border-indigo-900/50 bg-gradient-to-b from-indigo-50/70 via-indigo-50/30 to-white dark:from-indigo-950/30 dark:to-slate-900 shadow-[0_4px_14px_rgba(99,102,241,0.06)]',
                },
                {
                  wrapper: 'border-emerald-100 dark:border-emerald-900/50 bg-gradient-to-b from-emerald-50/70 via-emerald-50/30 to-white dark:from-emerald-950/30 dark:to-slate-900 shadow-[0_4px_14px_rgba(16,185,129,0.06)]',
                },
                {
                  wrapper: 'border-amber-100 dark:border-amber-900/50 bg-gradient-to-b from-amber-50/70 via-amber-50/30 to-white dark:from-amber-950/30 dark:to-slate-900 shadow-[0_4px_14px_rgba(245,158,11,0.06)]',
                },
              ];
              const style = STYLES[idx % STYLES.length];

              return (
                <div
                  key={figure.label}
                  className={cn(
                    'flex flex-col justify-between rounded-2xl border p-3.5 sm:p-4 transition-all hover:scale-[1.01] hover:shadow-md',
                    style.wrapper,
                  )}
                >
                  <div className="flex items-center justify-between text-sm font-semibold text-slate-600 dark:text-slate-300">
                    <span>{figure.label}</span>
                  </div>
                  <p className="mt-2 truncate text-[22px] font-black text-slate-900 dark:text-white tabular-nums tracking-tight leading-none">
                    {figure.value}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-[650] text-ink-2">
              {isAverage ? 'Logins on an average day' : 'Logins'}
            </h3>
            <BarChart
              title={isAverage ? 'Logins on an average day of the week' : `Logins per ${unit}`}
              series={[
                { key: 'web', label: 'Web', tone: 1 },
                { key: 'app', label: 'App', tone: 2 },
              ]}
              points={points((bucket) => ({
                web: bucket.web,
                app: bucket.app,
              }))}
              height={170}
              labelFromEnd
            />
          </div>

          {/* 2 Grid Charts: People & Time in the app */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Chart 2: People */}
            <div className="flex flex-col gap-2 rounded-xl border border-slate-100 dark:border-slate-800 p-3.5 sm:p-4">
              <h3 className="text-[13px] font-bold text-slate-800 dark:text-slate-200">People</h3>
              <LineChart
                title={`People per ${isAverage ? 'average day' : unit}`}
                series={[
                  { key: 'loggedIn', label: 'Logged in', tone: 1 },
                  { key: 'active', label: 'Active in the app', tone: 2 },
                ]}
                points={points((bucket) => ({
                  loggedIn: bucket.loggedInPeople,
                  active: bucket.activePeople,
                }))}
                height={170}
                labelFromEnd
              />
            </div>

            {/* Chart 3: Time on app */}
            <div className="flex flex-col gap-2 rounded-xl border border-slate-100 dark:border-slate-800 p-3.5 sm:p-4">
              <h3 className="text-[13px] font-bold text-slate-800 dark:text-slate-200">
                Time in the app
              </h3>
              <BarChart
                title={`Minutes in the app per ${isAverage ? 'average day' : unit}`}
                series={[{ key: 'minutes', label: 'Minutes', tone: 1 }]}
                points={points((bucket) => ({ minutes: bucket.minutes }))}
                height={170}
                formatValue={minutesLabel}
                labelFromEnd
              />
            </div>
          </div>

          <p className="text-2xs text-ink-3">
            “Active” and time in the app are counted from the day tracking began, and from the web
            app only for now. Logins cover web and mobile app, with their full history. A month
            counts each person once.
          </p>
        </>
      )}
    </section>
  );
}
